import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { PortalLink, tx } from '@akademi/site-builder-runtime';

/** A string an operator may author per locale (`{ en: "...", tr: "..." }`). */
export type Localized = string | Record<string, string>;

/** An image reference. `alt` is required; pass `""` only for a purely decorative image. */
export interface SkImageRef {
  src: string;
  alt?: Localized;
  width?: number;
  height?: number;
}

/** Localised text, trimmed. The runtime normally resolves locales before props arrive. */
export function text(value: unknown, fallback = ''): string {
  const resolved = tx(value).trim();
  return resolved || fallback;
}

/** Items of an array prop, ignoring anything that is not an object. */
export function items<T extends object>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter((item) => item && typeof item === 'object') as T[]) : [];
}

/**
 * Hrefs the storefront does NOT own must be plain anchors. `/kitchens/planner/` (with
 * the trailing slash) is a separate static app (PLAN.md, Contract 4): a
 * client-side Next navigation to it would be answered by the storefront's
 * catch-all route instead. `/planner` without the slash is the storefront's
 * own landing page and stays a normal link.
 */
export function isPlainAnchorHref(href: string): boolean {
  return (
    href.startsWith('/kitchens/planner/') ||
    /^(https?:|mailto:|tel:|sms:)/i.test(href) ||
    href.startsWith('#')
  );
}

export function SkLink({
  href,
  children,
  ...rest
}: { href: string | undefined; children?: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const target = href && href.trim() ? href.trim() : '#';
  if (isPlainAnchorHref(target)) {
    return (
      <a href={target} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <PortalLink href={target} {...rest}>
      {children}
    </PortalLink>
  );
}

export function SkImage({
  image,
  className,
  sizes,
  priority = false,
}: {
  image: SkImageRef | undefined;
  className?: string;
  sizes?: string;
  priority?: boolean;
}) {
  if (!image?.src) return null;
  return (
    <img
      className={className ? `sk-img ${className}` : 'sk-img'}
      src={image.src}
      alt={text(image.alt)}
      width={image.width}
      height={image.height}
      sizes={sizes}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...(priority ? { fetchPriority: 'high' as const } : {})}
    />
  );
}

/**
 * A heading with at most ONE bold key phrase, per the brief. `emphasis` is a
 * phrase inside `title`; when it is not found, the heading renders plain rather
 * than inventing a phrase.
 */
export function Emphasised({ title, emphasis }: { title: string; emphasis?: string }) {
  const phrase = emphasis?.trim();
  if (!phrase) return <>{title}</>;
  const index = title.indexOf(phrase);
  if (index < 0) return <>{title}</>;
  return (
    <>
      {title.slice(0, index)}
      <strong>{phrase}</strong>
      {title.slice(index + phrase.length)}
    </>
  );
}

/** A section heading tag chosen by the caller, so one block can sit under an h1 or an h2. */
export type HeadingLevel = 'h1' | 'h2' | 'h3';

export function headingLevel(value: unknown, fallback: HeadingLevel): HeadingLevel {
  return value === 'h1' || value === 'h2' || value === 'h3' ? value : fallback;
}

/** The level one step below `level`, for item headings inside a section. */
export function childLevel(level: HeadingLevel): 'h2' | 'h3' | 'h4' {
  return level === 'h1' ? 'h2' : level === 'h2' ? 'h3' : 'h4';
}

export type Surface = 'white' | 'warm' | 'sage-grey';

export function surfaceClass(value: unknown, fallback: Surface): string {
  const surface: Surface = value === 'white' || value === 'warm' || value === 'sage-grey' ? value : fallback;
  return `sk-surface-${surface}`;
}
